<template>
  <div class="r-cal">
    <!-- Header: prev / month-year buttons / next -->
    <div class="r-cal__header">
      <button
        type="button"
        class="r-cal__nav"
        aria-label="Previous"
        @click="prev"
      >
        <Icon type="chevron-left" />
      </button>

      <div class="r-cal__title">
        <!-- Date grid title: clickable month + year -->
        <template v-if="view === 'date'">
          <button
            type="button"
            class="r-cal__title-button"
            @click.stop="openMonthView"
          >
            {{ viewMonth.format('MMMM') }}
          </button>
          <button
            type="button"
            class="r-cal__title-button"
            @click.stop="openYearView"
          >
            {{ viewMonth.format('YYYY') }}
          </button>
        </template>
        <!-- Month grid title: clickable year -->
        <button
          v-else-if="view === 'month'"
          type="button"
          class="r-cal__title-button"
          @click.stop="openYearView"
        >
          {{ viewMonth.format('YYYY') }}
        </button>
        <!-- Year grid title: page range -->
        <span
          v-else
          class="r-cal__title-label"
        >
          {{ yearGroupTitle }}
        </span>
      </div>

      <button
        type="button"
        class="r-cal__nav"
        aria-label="Next"
        @click="next"
      >
        <Icon type="chevron-right" />
      </button>
    </div>

    <!-- Date grid -->
    <div v-if="view === 'date'">
      <div class="r-cal__dow-row">
        <div
          v-for="dow in weekdays"
          :key="dow"
          class="r-cal__dow"
        >
          {{ dow }}
        </div>
      </div>
      <div class="r-cal__rows">
        <div
          v-for="(week, weekIndex) in weeks"
          :key="weekIndex"
          class="r-cal__week"
          :class="{ 'is-week-hovered': isWeekHovered(week) }"
          @mouseover="onWeekMouseOver(week)"
          @mouseleave="onWeekMouseLeave"
        >
          <div
            v-for="day in week"
            :key="day.date.format('YYYY-MM-DD')"
            class="r-cal__day-cell"
            :class="{
              'is-disabled': day.isDisabled,
              'is-selected': isSelected(day.date),
              'is-in-range': isInRange(day.date),
              'is-range-start': isRangeStart(day.date),
              'is-range-end': isRangeEnd(day.date),
              'is-today': day.date.isToday(),
              'not-current-month': !day.isCurrentMonth
            }"
            @click="onDayClick(day)"
            @mouseover="hoveredDate = day.date"
            @mouseleave="hoveredDate = null"
          >
            <div class="r-cal__day">{{ day.day }}</div>
          </div>
        </div>
      </div>
    </div>

    <!-- Month grid (also the main view in month mode) -->
    <div
      v-else-if="view === 'month'"
      class="r-cal__month-grid"
    >
      <button
        v-for="(monthName, monthIndex) in monthNames"
        :key="monthName"
        type="button"
        class="r-cal__month-item"
        :class="{ 'is-selected': isMonthSelected(monthIndex) }"
        @click.stop="onMonthClick(monthIndex)"
      >
        {{ monthName }}
      </button>
    </div>

    <!-- Year grid -->
    <div
      v-else
      class="r-cal__year-grid"
    >
      <button
        v-for="year in years"
        :key="year"
        type="button"
        class="r-cal__year-item"
        :class="{ 'is-current-year': year === viewMonth.year() }"
        @click.stop="onYearClick(year)"
      >
        {{ year }}
      </button>
    </div>

    <!-- Time range inputs (range/week modes) -->
    <div
      v-if="showTimeRange && isRangeLike"
      class="r-cal__time-row"
    >
      <Input
        v-model="startTimeInput"
        size="sm"
        alt="Start time"
        placeholder="00:00"
        @blur="onTimeBlur('start', $event)"
      />
      <Input
        v-model="endTimeInput"
        size="sm"
        alt="End time"
        placeholder="23:59"
        @blur="onTimeBlur('end', $event)"
      />
    </div>

    <!-- Single time picker (single mode) -->
    <div
      v-if="showTime && props.mode === 'single'"
      class="r-cal__time-row"
    >
      <TimePicker
        :model-value="singleTimeInput"
        alt="Time"
        placeholder="00:00"
        @update:model-value="onSingleTimeChange"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import dayjs, { type Dayjs } from 'dayjs'
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter'
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore'
import isToday from 'dayjs/plugin/isToday'
import isoWeek from 'dayjs/plugin/isoWeek'
import { computed, ref, watch } from 'vue'
// components
import Input from '@/components/forms/Input.vue'
import TimePicker from '@/components/forms/TimePicker.vue'
import Icon from '@/components/icon/Icon.vue'
// composables
import { useDatePickerCalendar } from '@/composables/useDatePickerCalendar'
// types
import type { DatePickerDay, DatePickerMode, DateRange } from '@/types/components/dateTime'
// utilities
import { isValidTime } from '@/utilities/date-time'

dayjs.extend(isToday)
dayjs.extend(isoWeek)
dayjs.extend(isSameOrAfter)
dayjs.extend(isSameOrBefore)

/** Props for the standalone single-month Calendar */
const props = withDefaults(
  defineProps<{
    /** Selected value: 'YYYY-MM-DD[ HH:mm:ss]' string for single mode, DateRange for range/week/month modes */
    modelValue?: string | DateRange | null
    /** Selection mode */
    mode?: DatePickerMode
    /** Days before this date are disabled */
    minDate?: string | Date | null
    /** Days after this date are disabled */
    maxDate?: string | Date | null
    /** Specific days that are disabled */
    excludeDates?: Array<string | Date>
    /** Maximum number of days in a range (inclusive) */
    maxRange?: number | null
    /** Show start/end time inputs below the grid (range/week modes) */
    showTimeRange?: boolean
    /** Show a single time input below the grid (single mode) */
    showTime?: boolean
  }>(),
  {
    modelValue: null,
    mode: 'single',
    minDate: null,
    maxDate: null,
    excludeDates: () => [],
    maxRange: null,
    showTimeRange: false,
    showTime: false
  }
)

const emit = defineEmits<{
  'update:modelValue': [value: string | DateRange | null]
  /** Fired when a complete selection is made (day picked, range completed, week or month chosen) */
  select: [value: string | DateRange]
}>()

/** Whether the mode produces a DateRange value */
const isRangeLike = computed(() => props.mode !== 'single')

/** Whether emitted values include a time component */
const hasTimeComponent = computed(() => (props.mode === 'single' ? props.showTime : props.showTimeRange))

/** First click of an in-progress range selection */
const pendingStart = ref<Dayjs | null>(null)

/** Day currently hovered, used for the range preview */
const hoveredDate = ref<Dayjs | null>(null)

/** Week currently hovered (week mode highlight) */
const hoveredWeek = ref<DatePickerDay[] | null>(null)

// Time input drafts (committed on blur)
const startTimeInput = ref('00:00')
const endTimeInput = ref('23:59')
const singleTimeInput = ref('00:00')

/**
 * Parses a model date string leniently, returning null for empty/invalid input.
 */
function parseModelDate(value: string | null | undefined): Dayjs | null {
  // Nothing selected
  if (!value) return null
  const parsed = dayjs(value)
  return parsed.isValid() ? parsed : null
}

/** Selected single date derived from modelValue */
const selectedSingle = computed<Dayjs | null>(() => {
  // Only meaningful in single mode with a string model
  if (props.mode !== 'single' || typeof props.modelValue !== 'string') return null
  return parseModelDate(props.modelValue)
})

/** Selected range start derived from modelValue */
const selectedStart = computed<Dayjs | null>(() => {
  // Only meaningful in range-like modes with an object model
  if (!isRangeLike.value || !props.modelValue || typeof props.modelValue === 'string') return null
  return parseModelDate(props.modelValue.start)
})

/** Selected range end derived from modelValue */
const selectedEnd = computed<Dayjs | null>(() => {
  if (!isRangeLike.value || !props.modelValue || typeof props.modelValue === 'string') return null
  return parseModelDate(props.modelValue.end)
})

const {
  viewMonth,
  view,
  weeks,
  weekdays,
  monthNames,
  years,
  yearGroupTitle,
  next,
  prev,
  goToDate,
  openMonthView,
  openYearView,
  selectMonth,
  selectYear
} = useDatePickerCalendar({
  initialMonth: selectedStart.value ?? selectedSingle.value ?? dayjs(),
  minDate: () => props.minDate,
  maxDate: () => props.maxDate,
  excludeDates: () => props.excludeDates,
  maxRange: () => props.maxRange,
  rangeStart: pendingStart
})

// Month mode renders the month grid as its main view
if (props.mode === 'month') {
  view.value = 'month'
}

/**
 * Formats a boundary date for emission.
 * Start boundaries get :00 seconds; end boundaries get :59 seconds (minute-inclusive semantics).
 */
function formatBoundary(date: Dayjs, boundary: 'start' | 'end' | 'single'): string {
  // Date-only mode emits plain dates
  if (!hasTimeComponent.value) return date.format('YYYY-MM-DD')

  if (boundary === 'end') {
    const time = isValidTime(endTimeInput.value) ? endTimeInput.value : '23:59'
    return `${date.format('YYYY-MM-DD')} ${time.slice(0, 5)}:59`
  }
  const draft = boundary === 'start' ? startTimeInput.value : singleTimeInput.value
  const time = isValidTime(draft) ? draft : '00:00'
  return `${date.format('YYYY-MM-DD')} ${time.slice(0, 5)}:00`
}

/**
 * Emits a completed range (manual selection clears any preset on the model).
 */
function emitRange(start: Dayjs, end: Dayjs): void {
  const value: DateRange = {
    start: formatBoundary(start, 'start'),
    end: formatBoundary(end, 'end')
  }
  emit('update:modelValue', value)
  emit('select', value)
}

/**
 * Handles a click on a day cell according to the current mode.
 */
function onDayClick(day: DatePickerDay): void {
  // Disabled days are inert
  if (day.isDisabled) return

  if (props.mode === 'single') {
    const value = formatBoundary(day.date, 'single')
    emit('update:modelValue', value)
    emit('select', value)
    return
  }

  if (props.mode === 'week') {
    emitRange(day.date.startOf('isoWeek'), day.date.endOf('isoWeek'))
    return
  }

  // Range mode: first click starts the range, second click completes it
  if (!pendingStart.value) {
    pendingStart.value = day.date
  } else {
    const first = pendingStart.value
    const second = day.date
    pendingStart.value = null
    // Order the boundaries so start <= end
    if (second.isBefore(first, 'day')) {
      emitRange(second, first)
    } else {
      emitRange(first, second)
    }
  }
}

/**
 * Handles a click in the month grid: selects a month range in month mode,
 * otherwise drills back down to the date grid.
 */
function onMonthClick(monthIndex: number): void {
  if (props.mode === 'month') {
    const month = viewMonth.value.month(monthIndex)
    viewMonth.value = month.startOf('month')
    emitRange(month.startOf('month'), month.endOf('month'))
    return
  }
  selectMonth(monthIndex)
}

/**
 * Handles a click in the year grid (always moves on to the month grid).
 */
function onYearClick(year: number): void {
  selectYear(year)
}

/**
 * Whether a month cell matches the selected month (month mode only).
 */
function isMonthSelected(monthIndex: number): boolean {
  // Highlight only applies in month mode with a selection in the displayed year
  if (props.mode !== 'month' || !selectedStart.value) return false
  return selectedStart.value.year() === viewMonth.value.year() && selectedStart.value.month() === monthIndex
}

/**
 * Whether a day is one of the selected boundaries (or the pending range start).
 */
function isSelected(date: Dayjs): boolean {
  if (props.mode === 'single') {
    return !!selectedSingle.value && date.isSame(selectedSingle.value, 'day')
  }
  // A pending start takes over the display while picking the second date
  if (pendingStart.value) return date.isSame(pendingStart.value, 'day')
  return (
    (!!selectedStart.value && date.isSame(selectedStart.value, 'day')) ||
    (!!selectedEnd.value && date.isSame(selectedEnd.value, 'day'))
  )
}

/**
 * Whether a day sits inside the selected range (or the hover preview while picking).
 */
function isInRange(date: Dayjs): boolean {
  if (!isRangeLike.value) return false

  // Hover preview between the pending start and the hovered day
  if (pendingStart.value && hoveredDate.value) {
    const [lo, hi] = hoveredDate.value.isBefore(pendingStart.value, 'day')
      ? [hoveredDate.value, pendingStart.value]
      : [pendingStart.value, hoveredDate.value]
    return date.isSameOrAfter(lo, 'day') && date.isSameOrBefore(hi, 'day')
  }

  // Committed selection
  if (!selectedStart.value || !selectedEnd.value) return false
  return date.isSameOrAfter(selectedStart.value, 'day') && date.isSameOrBefore(selectedEnd.value, 'day')
}

/**
 * Whether a day is the visual start of the selected range.
 */
function isRangeStart(date: Dayjs): boolean {
  if (!isRangeLike.value) return false
  if (pendingStart.value && hoveredDate.value) {
    const lo = hoveredDate.value.isBefore(pendingStart.value, 'day') ? hoveredDate.value : pendingStart.value
    return date.isSame(lo, 'day')
  }
  return !!selectedStart.value && date.isSame(selectedStart.value, 'day')
}

/**
 * Whether a day is the visual end of the selected range.
 */
function isRangeEnd(date: Dayjs): boolean {
  if (!isRangeLike.value) return false
  if (pendingStart.value && hoveredDate.value) {
    const hi = hoveredDate.value.isBefore(pendingStart.value, 'day') ? pendingStart.value : hoveredDate.value
    return date.isSame(hi, 'day')
  }
  return !!selectedEnd.value && date.isSame(selectedEnd.value, 'day')
}

/**
 * Week hover highlight handlers (week mode only).
 */
function onWeekMouseOver(week: DatePickerDay[]): void {
  // Highlight only applies in week mode
  if (props.mode === 'week') hoveredWeek.value = week
}

function onWeekMouseLeave(): void {
  if (props.mode === 'week') hoveredWeek.value = null
}

function isWeekHovered(week: DatePickerDay[]): boolean {
  return props.mode === 'week' && hoveredWeek.value === week
}

/**
 * Commits a range time input on blur: validates, stores the draft, and re-emits
 * the current selection with the updated time.
 */
function onTimeBlur(which: 'start' | 'end', raw: string): void {
  const fallback = which === 'end' ? '23:59' : '00:00'
  const time = isValidTime(raw) ? raw : fallback

  if (which === 'start') startTimeInput.value = time
  else endTimeInput.value = time

  // Re-emit with the new time only when a complete range exists
  if (isRangeLike.value && selectedStart.value && selectedEnd.value) {
    emitRange(selectedStart.value, selectedEnd.value)
  }
}

/**
 * Handles a TimePicker change in single mode: stores the draft and re-emits
 * the selected day with the new time.
 */
function onSingleTimeChange(value: string): void {
  singleTimeInput.value = value.slice(0, 5)

  // Re-emit only when a day is already selected
  if (selectedSingle.value) {
    const updated = formatBoundary(selectedSingle.value, 'single')
    emit('update:modelValue', updated)
    emit('select', updated)
  }
}

// Keep the displayed month and time drafts in sync with external model changes
watch(
  () => props.modelValue,
  () => {
    const anchor = selectedStart.value ?? selectedSingle.value
    if (anchor) {
      goToDate(anchor)
      // Month mode stays on the month grid
      if (props.mode === 'month') view.value = 'month'
    }

    // Reflect model times back into the drafts
    if (hasTimeComponent.value) {
      if (selectedStart.value) startTimeInput.value = selectedStart.value.format('HH:mm')
      if (selectedEnd.value) endTimeInput.value = selectedEnd.value.format('HH:mm')
      if (selectedSingle.value) singleTimeInput.value = selectedSingle.value.format('HH:mm')
    }
  },
  { immediate: true }
)
</script>

<style>
.r-cal {
  display: flex;
  flex-direction: column;
  width: max-content;
  padding: var(--s-2);
  user-select: none;
}

.r-cal__header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: var(--s-4);
  margin-bottom: var(--s-3);
  font-weight: 600;
}

.r-cal__nav {
  cursor: pointer;
  height: 30px;
  width: 30px;
  border-radius: 50%;
  display: flex;
  justify-content: center;
  align-items: center;
  color: var(--text-quaternary);
  background: transparent;
  border: none;
}

.r-cal__nav:hover {
  color: var(--fg-brand);
  background-color: var(--bg-secondary);
}

.r-cal__title {
  display: flex;
  justify-content: center;
  min-width: 140px;
}

.r-cal__title-button {
  cursor: pointer;
  padding: var(--s-1) var(--s-2);
  border-radius: var(--rounded-md);
  font-size: var(--text-md);
  font-weight: 600;
  background: transparent;
  border: none;
  color: inherit;
}

.r-cal__title-button:hover {
  background-color: var(--bg-primary_hover);
}

.r-cal__title-label {
  padding: var(--s-1) var(--s-2);
  font-size: var(--text-md);
}

.r-cal__dow-row {
  display: flex;
  text-align: center;
  font-size: var(--text-sm);
  margin-bottom: var(--s-2);
  color: var(--text-secondary);
  font-weight: 500;
}

/* Header cells share the exact width of the day cells so columns line up */
.r-cal__dow {
  width: var(--s-9);
}

.r-cal__rows {
  display: flex;
  flex-direction: column;
  text-align: center;
}

.r-cal__week {
  display: flex;
}

.r-cal__day {
  height: var(--s-9);
  width: var(--s-9);
  display: flex;
  justify-content: center;
  align-items: center;
  border-radius: 50%;
  font-size: var(--text-md);
  color: var(--text-secondary);
  position: relative;
  transition: all 0.2s;
  cursor: pointer;
}

.r-cal__day-cell:hover > .r-cal__day {
  background-color: var(--bg-primary_hover);
}

.r-cal__week.is-week-hovered .r-cal__day {
  background-color: var(--bg-primary_hover);
}

.r-cal__day-cell.is-disabled > .r-cal__day {
  cursor: not-allowed;
  color: var(--text-disabled);
}

.r-cal__day-cell.not-current-month > .r-cal__day {
  color: var(--text-disabled);
}

.r-cal__day-cell.is-selected > .r-cal__day {
  background-color: var(--fg-brand);
  color: var(--text-white);
  font-weight: 600;
}

.r-cal__day-cell.is-in-range {
  background-color: var(--bg-brand);
  color: var(--text-brand);
}

.r-cal__day-cell.is-range-start {
  border-top-left-radius: 50%;
  border-bottom-left-radius: 50%;
}

.r-cal__day-cell.is-range-end {
  border-top-right-radius: 50%;
  border-bottom-right-radius: 50%;
}

.r-cal__day-cell.is-today > .r-cal__day::after {
  content: '';
  height: 4px;
  width: 4px;
  position: absolute;
  bottom: 5px;
  left: calc(50% - 2px);
  background-color: var(--text-secondary);
  border-radius: 50%;
}

.r-cal__month-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: var(--s-2);
  width: 100%;
}

.r-cal__month-item,
.r-cal__year-item {
  cursor: pointer;
  padding: var(--s-2);
  text-align: center;
  border-radius: var(--rounded-md);
  font-size: var(--text-md);
  background: transparent;
  border: none;
  color: inherit;
}

.r-cal__month-item:hover,
.r-cal__year-item:hover {
  background-color: var(--bg-primary_hover);
}

.r-cal__month-item.is-selected {
  background-color: var(--fg-brand);
  color: var(--text-white);
  font-weight: 600;
}

.r-cal__year-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: var(--s-2);
  width: 100%;
}

.r-cal__year-item.is-current-year {
  font-weight: bold;
  color: var(--fg-brand);
}

.r-cal__time-row {
  display: flex;
  gap: var(--s-2);
  margin-top: var(--s-3);
  padding-top: var(--s-3);
  border-top: 1px solid var(--border-secondary);
  /* Match the calendar grid width without contributing intrinsic width,
     so wide inputs can't stretch the dropdown out of alignment */
  width: 0;
  min-width: 100%;
}

.r-cal__time-row > div {
  flex: 1 1 0;
  min-width: 0;
}

.r-cal__time-row input {
  min-width: 0;
}
</style>
